import React, { useEffect, useRef, useState } from 'react'

const DropdownMenuContext = React.createContext()

const DropdownMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <DropdownMenuContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  )
}

const DropdownMenuTrigger = ({ children, asChild = false, ...props }) => {
  const { isOpen, setIsOpen } = React.useContext(DropdownMenuContext)

  const handleClick = (e) => {
    e.preventDefault()
    setIsOpen(!isOpen)
  }

  if (asChild) {
    return React.cloneElement(children, {
      onClick: handleClick,
      'aria-expanded': isOpen,
      'aria-haspopup': true,
      ...props
    })
  }
  return (
    <button
      onClick={handleClick}
      aria-expanded={isOpen}
      aria-haspopup={true}
      {...props}
    >
      {children}
    </button>
  )
}

const DropdownMenuContent = ({ children, align = 'end', className = '', ...props }) => {
  const { isOpen } = React.useContext(DropdownMenuContext)

  const alignClasses = {
    start: 'left-0',
    end: 'right-0',
    center: 'left-1/2 transform -translate-x-1/2'
  }

  if (!isOpen) return null

  return (
    <div
      className={`absolute top-full mt-1 z-[9999] min-w-[8rem] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1 text-gray-950 dark:text-gray-100 shadow-lg ${alignClasses[align]} ${className}`}
      role="menu"
      style={{ zIndex: 9999 }}
      {...props}
    >
      {children}
    </div>
  )
}

const DropdownMenuLabel = ({ children, className = '', ...props }) => (
  <div className={`px-2 py-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 ${className}`} {...props}>
    {children}
  </div>
)

const DropdownMenuItem = ({ children, className = '', onClick, ...props }) => {
  const { setIsOpen } = React.useContext(DropdownMenuContext)

  const handleClick = (e) => {
    if (onClick) {
      onClick(e)
    }
    setIsOpen(false) // 點擊選項後自動關閉選單
  }

  return (
    <button
      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:text-gray-900 dark:focus:text-gray-100 text-gray-900 dark:text-gray-100 ${className}`}
      onClick={handleClick}
      role="menuitem"
      {...props}
    >
      {children}
    </button>
  )
}

const DropdownMenuSeparator = ({ className = '', ...props }) => (
  <div className={`-mx-1 my-1 h-px bg-gray-200 dark:bg-gray-600 ${className}`} {...props} />
)

export {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
}

